<template>
    <aside
        class="relative shrink-0 aside_height overflow-hidden print:!h-auto print:w-full"
    >
        <div
            class="fixed border-keyline-primary bg-gray-tier-3 border-l overflow-auto aside_height pb-16 w-[var(--app-detail-aside-width)] print:border-none print:!h-auto print:pb-0 print:static"
        >
            <div class="relative z-0">
                <div key="j">
                    <div class="relative px-8 pt-8 pb-6 z-10">
                        <Link
                            :href="
                                route(
                                    'web.catalog.manufacturers.show',
                                    product?.vendor?.slug
                                )
                            "
                            class="font-medium type-secondary hover:underline"
                        >
                            {{ product?.vendor?.display_name }}
                        </Link>
                        <div
                            class="font-bold type-primary text-2xl leading-tight uppercase"
                        >
                            {{ product?.sku }}
                        </div>
                        <template
                            v-if="description"
                        >
                            <p
                                class="type-secondary"
                                v-html="description"
                            ></p>
                        </template>
                    </div>
                    <div
                        v-if="finish_swatch && finish.value"
                        class="group pl-8 relative w-fit flex items-center gap-1.5 text-base mb-6"
                    >
                        <img
                            class="rounded-full w-6 h-6 p-0.5 border border-gray-300"
                            @mouseover="hintSeen = true"
                            @mouseleave="hintSeen = false"
                            :src="finish_swatch"
                            :alt="finish.description"
                        />
                        <span>{{ finish.value }}</span>
                        <HintTransition
                            :hint-seen="hintSeen"
                            name="fade-right"
                            >{{ finish.description }}</HintTransition
                        >
                    </div>
                    <!--                    <LoadingSpinner-->
                    <!--                        v-if="isLoadingMeta || !pricing?.price || !availability"-->
                    <!--                        class="mx-8 mb-6"-->
                    <!--                        :large="true"-->
                    <!--                    />-->
                    <div
                        v-if="isLoadingMeta"
                    >
                        <div class="px-8 mb-6 flex items-baseline gap-3">
                            <div
                                class="w-20 bg-gray-tier-5 animate-pulse p-4 rounded"
                            ></div>
                            <div
                                class="w-44 bg-gray-tier-5 animate-pulse p-3 rounded"
                            ></div>
                        </div>
                        <Divider class="mb-6" />
                        <div class="px-8 flex items-baseline gap-6 mb-6">
                            <div
                                class="w-32 bg-gray-tier-5 animate-pulse p-3 rounded"
                            ></div>
                            <div
                                class="w-52 bg-gray-tier-5 animate-pulse p-3 rounded"
                            ></div>
                        </div>
                    </div>

                    <!-- customer pricing -->
                    <div v-else class="p-8 pt-0">
                        <div class="flex items-end gap-3">
                            <div
                                v-if="
                                    availability?.total < 1 && product.status == 'D'
                                "
                                class="font-medium text-orange"
                            >
                                {{ $t("Discontinued") }}
                            </div>
                            <div v-else-if="pricing?.price > 0 && !['S','N'].includes(product?.status)">
                                <strong
                                    class="!leading-none text-lg at1200:text-xl"
                                >
                                    {{ showMeTheMoney(pricing.price) }}
                                </strong>
                            </div>
                            <div v-else>
                                <ChatButton />
                            </div>

                            <div>
                                <div
                                    v-if="product.pricing_warehouse_item.list_price > 0 && !['S','N'].includes(product?.status)"
                                    class="type-secondary"
                                >
                                    {{
                                        showMeTheMoney(
                                            product.pricing_warehouse_item
                                                .list_price
                                        )
                                    }}
                                    {{ $t("list") }}
                                    <span
                                        v-if="product.pricing_warehouse_item.fut_list_eff_date && product.pricing_warehouse_item.last_fut_update && product.pricing_warehouse_item.fut_list_price"
                                    >
                                        ({{
                                            product.pricing_warehouse_item
                                                .fut_list_price == 0
                                                ? DateTime.fromISO(
                                                    product.pricing_warehouse_item
                                                        .fut_list_eff_date
                                                ).toFormat("D")
                                                : DateTime.fromISO(
                                                    product.pricing_warehouse_item
                                                        .last_fut_update
                                                ).toFormat("D")
                                        }})
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- pricing changes -->
                        <div
                            v-if="
                                product.pricing_warehouse_item.list_price >
                                    0 &&
                                product.pricing_warehouse_item
                                    .fut_list_price > 0 &&
                                product.pricing_warehouse_item
                                    .fut_list_price !==
                                    product.pricing_warehouse_item
                                        .list_price
                            "
                            class="type-secondary"
                        >
                            {{ $t("List price") }}
                            {{
                                product.pricing_warehouse_item
                                    .fut_list_price >
                                product.pricing_warehouse_item.list_price
                                    ? $t("increase")
                                    : $t("decrease")
                            }}
                            {{ $t("to") }}
                            {{
                                showMeTheMoney(
                                    product.pricing_warehouse_item
                                        .fut_list_price
                                )
                            }}
                            {{ $t("on") }}
                            {{
                                DateTime.fromISO(
                                    product.pricing_warehouse_item
                                        .fut_list_eff_date
                                ).toFormat("D")
                            }}
                        </div>

                        <SurchargeNotice
                            :surcharge="surcharge"
                            :productSlug="product?.slug"
                            :isSourcing="true"
                            class="mt-6"
                        />
                    </div>

                    <!-- availability -->
                    <div
                        v-if="availability"
                        class="flex w-full items-center justify-between px-8 py-5 border-t border-keyline-primary"
                    >
                        <AvailabilityList
                            :availability="availability"
                            :pricing="pricing"
                            :card="card"
                            :isLoadingMeta="isLoadingMeta"
                            class="type-secondary"
                        >
                            <BaseSvg
                                name="icon-check-outlined"
                                class="shrink-0"
                                v-if="availability?.total > 0 && pricing?.price"
                            />
                        </AvailabilityList>
                    </div>

                    <!-- SEC-3099  -->
                    <template v-if="user && master_note_restriction?.length">
                        <div class="sm-well-orange items-center mx-8 mb-6 p-4 text-sm rounded">
                            <span class="font-bold">Restriction: </span>
                            <span class="if-list">
                                <span>{{master_note_restriction}}</span>
                            </span>
                        </div>
                    </template>

                    <!-- SEC-2342 -->
                    <template v-if="product.series?.product_alert">
                        <div class="sm-well-orange items-center mx-8 p-4 text-sm rounded">
                            <span class="if-list">
                                <span v-html="product.series?.product_alert"></span>
                            </span>
                        </div>
                    </template>
                    <template v-else >
                        <div
                            v-if="options && Object.keys(options).length > 0"
                            class="right-0 left-0 type-secondary w-full relative print:hidden"
                        >
                            <div class="px-8 space-y-3">
                                <div
                                    v-for="(group, group_key) in options"
                                    :id="group_key"
                                >
                                    <template v-for="(options, key) in group">
                                        <SearchSelect
                                            class="mb-3"
                                            :options="options"
                                            :label="key"
                                            v-model="productOptions[key]"
                                            :default-value="
                                                Object.keys(
                                                    _.pickBy(options, (value) => {
                                                        return (
                                                            value
                                                                .toLowerCase()
                                                                .includes('std') ||
                                                            value
                                                                .toLowerCase()
                                                                .includes(
                                                                    'standard'
                                                                )
                                                        );
                                                    })
                                                )[0]
                                            "
                                        />
                                    </template>
                                </div>
                            </div>
                        </div>

                        <p
                            class="sm-well-blue p-4 mx-8 text-sm rounded mb-3"
                            v-if="_.get(card,'legacy_tags',[]).includes('value-center')"
                        >
                            This product is currently part of our Value Center promotion and aggressively discounted off of list price. This is a discontinued, overstock or closeout item. Sale is subject to stock on-hand. No factory or backorders will be accepted. We reserve the right to limit quantity sold. All sales are final (no returns).
                        </p>

                        <div class="px-8 py-4 z-10 bg-gray-tier-3 print:hidden">
                            <div
                                v-if="pricing?.price && (product?.status !== 'D' || availability?.total > 0) && !['S','N'].includes(product?.status)"
                                class="flex items-center gap-4 mb-4 h-10"
                            >
                                <Counter v-model="quantity" />
                                <button
                                    @click="
                                        eventBus.emit('cart:add-item', {
                                            sku:card.item_id,
                                            quantity,
                                            options: { specification_group: specification_group },
                                            meta: {...meta, 'has_finish_alert': hasFinishAlert },
                                        })
                                    "
                                    :disabled="isLoading || inert"
                                    class="whitespace-nowrap btn-app-lg-white-on-black w-full justify-center gap-2 h-10 capitalize"
                                >
                                    <BaseSvg name="icon-shopping-cart" />
                                    {{ $t("Add to cart") }}
                                </button>
                            </div>
                            <p
                                v-if="isBuildable"
                                class="type-secondary text-sm text-left pt-2"
                            >
                                {{
                                    $t("We build to configure the exact specs for")
                                }}
                                <a href="#" class="type-primary">{{
                                    product.sku
                                }}</a>
                                {{ $t("from components on-hand.") }}
                                <span
                                    class="decoration-solid underline underline-offset-4 hover:type-primary cursor-pointer"
                                    @click="openChat"
                                >
                                    {{ $t("Start a build request.") }}
                                </span>
                            </p>
                        </div>
                        <AddedToList />
                    </template>
                </div>
            </div>
        </div>
    </aside>
</template>

<script setup>
import {
    AvailabilityList,
    BaseSvg,
    Counter,
    Divider,
    HintTransition,
    SearchSelect,
    ChatButton,
    SurchargeNotice,
    AddedToList,
} from "@/Components";
import {
    eventBus,
    useRestrictions,
    useUser,
    useHelpers,
    useSegment,
    useChat,
} from "@/Composables";
import { computed, inject, onMounted, ref, watch } from "vue";
import { DateTime } from "luxon";
import _ from "lodash";

const { user } = useUser();

const { showMeTheMoney } = useHelpers();
const { track } = useSegment();

const { showNewMessage } = useChat();
const openChat = () => {
    let message = "Please let me know build quantities for " + (props.card ? props.card.item_id : "") + "."
    showNewMessage(message)
};

const route = inject("route");
const props = defineProps([
    "product",
    "pricing",
    "availability",
    "finish",
    "options",
    "card",
    "isLoadingMeta",
    "customer",
    'finish_swatch',
    "master_note_restriction",
    "surcharge",
]);

const hintSeen = ref(false);

let quantity = ref(1);
let productOptions = ref({});
let meta = ref([]);
const { restrictions } = useRestrictions(props.product?.attributes ?? {});

const specification_group = computed(() => {
    let specification_group = {};
    Object.entries(productOptions.value).map((option) => {
        let spec_group = option[0];
        let option_key = option[1];
        Object.entries(props.options).map((options) => {
            return Object.entries(options[1]).map((option) => {
                if (option[0] === spec_group) {
                    specification_group[spec_group] = {
                        [option_key]: option[1][option_key],
                    };
                }
            });
        });
    });
    return specification_group;
});

// This is the old EE code logic for showing the build request button.
// {if sld:ser_zero_inventory && (total < 1 && status != 'Discontinued') && '{exp:stash:priority}' != 1}
const isBuildable = computed(() => props.product.series?.is_buildable &&
    props.availability?.total < 1 &&
    !['D'].includes(props.product.status) &&
    !(['A'].includes(props.product.status) &&
        props.product.vendor?.is_inventory_plus &&
        ['Sargent','LCN','Schlage','Von Duprin','Schlage Electronics'].includes(props.product.vendor?.display_name))
)

const description = computed(() => {
    let desc = _.get(props.product, "descriptions.product-description.0.short", "");
    if (!desc) {
        desc = _.get(props.card, "display_short_description", "");
    }
    return desc;
});

const hasFinishAlert = computed(() => {
    let finish = props.finish.value;
    return (finish?.startsWith("625") || finish?.startsWith("629"))
});

onMounted(() => {
    watch(isBuildable, (buildable) => buildable && track.zeroInventoryProductViewed(props.product.sku))
})

</script>

<style scoped>
.aside_height {
    height: calc(
        100vh - (var(--app-header-height) + var(--app-sub-nav-height))
    );
}
</style>
